import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import Icon from 'flow/components/ui-components/Icon';
import { MeetingLocation } from 'flow/utils/meeting-constants';
import { sendUserActive } from 'flow/actions/meeting-actions';

import { twMerge } from 'tailwind-merge';
import {
    selectLobbyAudioActive,
    selectSenderAudioActive,
} from 'flow/selectors/meeting-selectors';

interface Props {
    meetingLocation:
        | typeof MeetingLocation.LOBBY
        | typeof MeetingLocation.ACTIVE_MEETING;
    audioStream?: MediaStream | null;
}

const MeetingAudioIndicator: React.FC<Props> = ({
    meetingLocation,
    audioStream,
}) => {
    const dispatch = useAppDispatch();

    const lobbyAudioActive = useAppSelector(selectLobbyAudioActive);
    const activeMeetingAudioActive = useAppSelector(selectSenderAudioActive);

    const renderSmallSize = meetingLocation === MeetingLocation.ACTIVE_MEETING;
    const barCount = 3;

    const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
    const animationFrameId = useRef<number>(0);
    const barElementRef = useRef<HTMLDivElement | null>(null);
    const isUserTalking = useRef<boolean>(false);
    const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
    const [audioActive, setAudioActive] = useState<boolean>(
        meetingLocation === MeetingLocation.ACTIVE_MEETING
            ? activeMeetingAudioActive
            : lobbyAudioActive,
    );

    useEffect(() => {
        if (!audioContext) {
            const context = new AudioContext();
            setAudioContext(context);
        }

        if (!analyser && audioContext) {
            const newAnalyser = audioContext.createAnalyser();
            newAnalyser.fftSize = 32; // Must be a power of 2 between 2^5 and 2^15
            newAnalyser.smoothingTimeConstant = 0.85;
            setAnalyser(newAnalyser);
        }

        return () => {
            cancelAnimationFrame(animationFrameId.current);
        };
    }, [setAnalyser, audioContext, analyser]);

    // Hand the context back when the indicator goes away. The effect above
    // cannot do it: it re-runs while setting the analyser up, so its cleanup
    // would close the context moments after creating it. Without this, every
    // visit to the lobby leaves one behind, and a browser grants only a few.
    useEffect(
        () => () => {
            audioContext?.close().catch(() => undefined);
        },
        [audioContext],
    );

    const checkActivityIntervalCallback = useCallback(() => {
        if (
            isUserTalking.current &&
            meetingLocation === MeetingLocation.ACTIVE_MEETING
        ) {
            dispatch(sendUserActive());
            isUserTalking.current = false;
        }
    }, [dispatch, meetingLocation]);

    useEffect(() => {
        const activityInterval = setInterval(
            checkActivityIntervalCallback,
            1000,
        );
        return () => {
            clearInterval(activityInterval);
            cancelAnimationFrame(animationFrameId.current);
        };
    }, [checkActivityIntervalCallback]);

    const setDefaultBarState = useCallback(
        (barElements: NodeListOf<ChildNode>) => {
            for (let i = 0; i < barCount; i++) {
                const element = barElements[i] as HTMLElement;
                const elementStyle = element?.style;
                if (elementStyle) {
                    elementStyle.transform = 'scaleY(0.25)';
                }
            }
        },
        [],
    );

    const updateBarWithValue = useCallback(
        (element: HTMLElement, value: number) => {
            const elementStyle = element.style;
            if (value > 0.25) {
                if (!isUserTalking.current) {
                    isUserTalking.current = true;
                }
                elementStyle.transform = `scaleY(${value})`;
            } else {
                elementStyle.transform = 'scaleY(0.25)';
            }
            elementStyle.opacity = Math.max(0.3, value).toString();
        },
        [],
    );

    const manipulateAudioBars = useCallback(
        (data: Uint8Array | null, barElements: NodeListOf<ChildNode>) => {
            if (!audioActive) {
                console.warn(
                    'Audio indicator: audio not active, stopping manipulateAudioBars',
                );
                cancelAnimationFrame(animationFrameId.current);
                return;
            }

            if (!data) {
                console.warn(
                    'Audio indicator: no data in manipulateAudioBars, setting default bar state',
                );
                setDefaultBarState(barElements);
                return;
            }

            const sizeMapper = [5, 2, 6];
            const values = Object.values(data);
            for (let i = 0; i < barCount; i++) {
                const value = values[sizeMapper[i]] / 255;
                const element = barElements[i] as HTMLElement;
                if (element?.style) {
                    updateBarWithValue(element, value);
                }
            }
        },
        [audioActive, setDefaultBarState, updateBarWithValue],
    );

    const startBarsAnimation = useCallback(() => {
        cancelAnimationFrame(animationFrameId.current);
        const barElements = barElementRef.current?.childNodes;

        if (!audioStream || !analyser || !audioContext || !barElements) {
            console.warn(
                'Audio indicator #startBarsAnimation issue:',
                audioStream,
                analyser,
                audioContext,
            );
            return;
        }

        const source = audioContext.createMediaStreamSource(audioStream);
        source.connect(analyser);
        const frequencyData = new Uint8Array(analyser.frequencyBinCount);
        const renderBars = () => {
            analyser.getByteFrequencyData(frequencyData);
            manipulateAudioBars(frequencyData, barElements);
            if (audioActive) {
                // We want to end the recursion if the audio got muted
                animationFrameId.current = requestAnimationFrame(renderBars);
            }
        };
        requestAnimationFrame(renderBars);
    }, [analyser, audioStream, audioActive, audioContext, manipulateAudioBars]);

    useEffect(() => {
        const relevantAudioActive =
            meetingLocation === MeetingLocation.ACTIVE_MEETING
                ? activeMeetingAudioActive
                : lobbyAudioActive;
        setAudioActive(relevantAudioActive);

        if (relevantAudioActive && audioStream && analyser && audioContext) {
            startBarsAnimation();
        } else {
            cancelAnimationFrame(animationFrameId.current);
        }
    }, [
        analyser,
        audioActive,
        audioContext,
        audioStream,
        startBarsAnimation,
        meetingLocation,
        activeMeetingAudioActive,
        lobbyAudioActive,
    ]);

    const renderAudioBarDivs = () => {
        const barList = [];
        for (let i = 0; i < barCount; i++) {
            barList.push({ key: `bar${i}` });
        }
        return (
            <>
                {barList.map((element) => (
                    <div
                        className={twMerge(
                            'bg-green inline-block h-full w-[3px] scale-y-[0.5] transform rounded-[3px] border border-transparent opacity-30',
                            renderSmallSize && 'w-[1px]',
                        )}
                        key={element.key}
                    />
                ))}
            </>
        );
    };

    return (
        <>
            {audioActive ? (
                <div
                    ref={barElementRef}
                    className={twMerge(
                        'box-border inline-flex h-8 w-8 items-center justify-evenly px-1 py-0.5',
                        renderSmallSize && 'h-4 w-4 p-0.5',
                    )}
                >
                    {renderAudioBarDivs()}
                </div>
            ) : (
                <div
                    className={twMerge(
                        'box-border inline-flex h-8 w-8 items-center justify-center',
                        renderSmallSize && 'h-4 w-4',
                    )}
                >
                    <Icon
                        iconKey="mic_off"
                        size={renderSmallSize ? '16px' : '18px'}
                    />
                </div>
            )}
        </>
    );
};

export default MeetingAudioIndicator;
